import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { UserProfile, Organization, TIER_LIMITS, SubscriptionTier } from '../types';
import { searchOrganizations } from '../services/firebase';
import {
  upgradeToMinistry,
  downgradeToFree,
  getSubscriptionStatus,
  getMonthlyQuotaStatus
} from '../services/aiQuotaService';

interface SubscriptionAdminProps {
  user: UserProfile;
  onLogout: () => void;
}

interface OrgSubscriptionDetails {
  org: Organization;
  tier: SubscriptionTier;
  upgradedAt?: number;
  upgradedBy?: string;
  quota: {
    images: { used: number; limit: number; remaining: number };
    tts: { used: number; limit: number; remaining: number };
    videos: { used: number; limit: number; remaining: number };
  };
}

export const SubscriptionAdmin: React.FC<SubscriptionAdminProps> = ({
  user,
  onLogout
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgSubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Check if user is authorized (only jonstewart24@gmail.com)
  const isAuthorized = user.email === 'jonstewart24@gmail.com';

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError(null);
    setSelectedOrg(null);

    try {
      const results = await searchOrganizations(searchTerm);
      setSearchResults(results);

      if (results.length === 0) {
        setError('No organizations found matching your search.');
      }
    } catch (err: any) {
      console.error('Search failed:', err);
      setError(err.message || 'Failed to search organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = async (org: Organization) => {
    setLoading(true);
    setError(null);

    try {
      const [subscriptionStatus, quotaStatus] = await Promise.all([
        getSubscriptionStatus(org.id),
        getMonthlyQuotaStatus(org.id)
      ]);

      setSelectedOrg({
        org,
        tier: subscriptionStatus.tier,
        upgradedAt: subscriptionStatus.upgradedAt,
        upgradedBy: subscriptionStatus.upgradedBy,
        quota: {
          images: quotaStatus.images,
          tts: quotaStatus.tts,
          videos: quotaStatus.videos
        }
      });
    } catch (err: any) {
      console.error('Failed to load org details:', err);
      setError(err.message || 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedOrg) return;

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await upgradeToMinistry(selectedOrg.org.id, user.uid);

      // Refresh the org details
      await handleSelectOrg(selectedOrg.org);
      setSuccess(`Successfully upgraded ${selectedOrg.org.name} to Ministry tier!`);
    } catch (err: any) {
      console.error('Upgrade failed:', err);
      setError(err.message || 'Failed to upgrade organization');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!selectedOrg) return;

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await downgradeToFree(selectedOrg.org.id);

      // Refresh the org details
      await handleSelectOrg(selectedOrg.org);
      setSuccess(`Successfully downgraded ${selectedOrg.org.name} to Free tier.`);
    } catch (err: any) {
      console.error('Downgrade failed:', err);
      setError(err.message || 'Failed to downgrade organization');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple header for this secret admin page */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <Icons.ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <button
            onClick={onLogout}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">
            Subscription Management
          </h1>
          <p className="text-gray-600">
            Upgrade or downgrade organization subscriptions.
          </p>
        </div>

        {/* Tier Info Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icons.User className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Free Tier</h3>
                <p className="text-sm text-gray-500">Lifetime starter credits</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.free.images} images (lifetime)
              </li>
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.free.tts} TTS (lifetime)
              </li>
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.free.videos} videos (lifetime)
              </li>
            </ul>
            <p className="text-xs text-gray-500 mt-3">Credits never reset - upgrade when exhausted</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icons.Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Ministry Tier</h3>
                <p className="text-sm text-blue-600">$24.99/month</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.ministry.images} images/month
              </li>
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.ministry.tts} TTS/month
              </li>
              <li className="flex items-center gap-2">
                <Icons.Check className="w-4 h-4 text-green-500" />
                {TIER_LIMITS.ministry.videos} videos/month
              </li>
            </ul>
            <p className="text-xs text-blue-600 mt-3">Resets on the 1st of each month</p>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Find Organization</h2>

          <div className="flex gap-3">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by organization name or code..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              onClick={handleSearch}
              disabled={loading || !searchTerm.trim()}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <Icons.Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Icons.Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500 mb-3">
                Found {searchResults.length} organization(s):
              </p>
              <div className="space-y-2">
                {searchResults.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleSelectOrg(org)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                      selectedOrg?.org.id === org.id
                        ? 'border-primary bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{org.name}</p>
                        <p className="text-sm text-gray-500">Code: {org.code}</p>
                      </div>
                      <Icons.Next className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {/* Selected Organization Details */}
        {selectedOrg && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedOrg.org.name}</h2>
                <p className="text-sm text-gray-500">Code: {selectedOrg.org.code}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                selectedOrg.tier === 'ministry'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {selectedOrg.tier === 'ministry' ? 'Ministry' : 'Free'} Tier
              </span>
            </div>

            {/* Current Usage */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Current Month Usage</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Images</p>
                  <p className="text-xl font-bold text-gray-900">
                    {selectedOrg.quota.images.used}/{selectedOrg.quota.images.limit}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">TTS</p>
                  <p className="text-xl font-bold text-gray-900">
                    {selectedOrg.quota.tts.used}/{selectedOrg.quota.tts.limit}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Videos</p>
                  <p className="text-xl font-bold text-gray-900">
                    {selectedOrg.quota.videos.used}/{selectedOrg.quota.videos.limit}
                  </p>
                </div>
              </div>
            </div>

            {/* Upgrade Info */}
            {selectedOrg.upgradedAt && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  Upgraded on {formatDate(selectedOrg.upgradedAt)}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {selectedOrg.tier === 'free' ? (
                <button
                  onClick={handleUpgrade}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <Icons.Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icons.Award className="w-4 h-4" />
                  )}
                  Upgrade to Ministry
                </button>
              ) : (
                <button
                  onClick={handleDowngrade}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <Icons.Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icons.X className="w-4 h-4" />
                  )}
                  Downgrade to Free
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
